import { createContext, useRef } from "react"
import { Outlet } from "react-router-dom"
import { useAppDispatch } from "../state/hooks"
import { useLazyQuery } from "@apollo/client"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import {
  endOfDay,
  IANATimeZone,
  nowDateTime,
  removeDays,
  startOfDay,
  toTimestamp,
  toUserTZ,
} from "@hydrologiq/common/helpers/date"
import { TimePoint } from "@hydrologiq/common/types/graph/timeseries"
import { convertTimeSeriesData } from "@hydrologiq/common/types/graph/timeseries"
import { convertTimeSerieData } from "@hydrologiq/common/types/graph/timeseries"
import { hydrogenForecast } from "../asset/attributes/rundry/hydrogen"
import { setRunDry } from "../state/slices/forecastSlice"

type ContextState = {
  getForecast: (assignments: gqlTypes.Assignment | gqlTypes.Assignment[], asset?: string | string[]) => Promise<void>
  loading: boolean
}

export const ForecastContext = createContext<ContextState | undefined>(undefined)

export const ForecastProvider = ({ children }: { children?: React.ReactNode }) => {
  const dispatch = useAppDispatch()

  const [getForecast, { loading: loadingForecast }] = useLazyQuery(
    gqlTypes.IoTDataHydrogenWeightForecastDownSampledDocument
  )
  const [getForecastAsset, { loading: loadingForecastAsset }] = useLazyQuery(
    gqlTypes.IoTDataHydrogenWeightForecastAssetDocument
  )

  const runDry = useRef<string[]>([])

  const forecast = async (asset: string, assignment: string, installation: string, start: string, end?: string) => {
    const window = 7
    const threshold = 1

    const nowDate = nowDateTime()
    const installationStartDate = toUserTZ(start)
    const installationEndDate = end ? toUserTZ(end) : undefined
    const installationEnded = installationEndDate && nowDate.unixUtcMillis() >= installationEndDate.unixUtcMillis()
    const latestEndDate = installationEnded ? installationEndDate : nowDate
    const latestStartDate = removeDays(latestEndDate, window)
    const diffEndDate = latestEndDate
    const diffStartDate = removeDays(diffEndDate, window)
    ;(!installation && asset ? getForecastAsset : getForecast)({
      variables: {
        installations: installation,
        assets: asset,
        latestStart: toTimestamp(startOfDay(latestStartDate), true),
        latestStop: toTimestamp(installationEnded ? endOfDay(latestEndDate) : latestEndDate, true),
        diffStart: toTimestamp(startOfDay(diffStartDate), true),
        diffStop: toTimestamp(endOfDay(diffEndDate), true),
        installationStart: toTimestamp(startOfDay(installationStartDate), true),
        location: IANATimeZone(),
      },
    }).then((result) => {
      const data = result.data

      let forecastedDays: number | null = null
      let currentWeight: TimePoint | undefined = undefined
      if (data) {
        const diffValues = convertTimeSeriesData(data.difference as gqlTypes.TimeSeriesData[])

        currentWeight = convertTimeSerieData(data.weight as gqlTypes.TimeSeriesData)
        let weightToForecast = structuredClone(currentWeight)

        if (data.weightMin) {
          const weight = convertTimeSerieData(data.weightMin as gqlTypes.TimeSeriesData).value
          weightToForecast.value = weightToForecast.value - weight
        }
        forecastedDays = hydrogenForecast(weightToForecast, diffValues, threshold, true)
        dispatch(setRunDry([assignment, { days: forecastedDays ?? undefined, timestamp: nowDate.unixUtcMillis() }]))
      }
    })
  }

  const value = {
    getForecast: async (assignments: gqlTypes.Assignment | gqlTypes.Assignment[], asset?: string | string[]) => {
      let index = 0
      for (const assignment of Array.isArray(assignments) ? assignments : [assignments]) {
        const assetId = (Array.isArray(asset) ? asset[index] : asset) ?? assignment.asset
        if (runDry.current.includes(assignment.id)) continue
        runDry.current.push(assignment.id)
        await forecast(
          assetId ?? "",
          assignment.id,
          assignment.installation?.id ?? "",
          assignment.start,
          assignment.end ?? undefined
        )
        index++
      }
    },
    loading: loadingForecast,
  }

  return <ForecastContext.Provider value={value}>{children ? children : <Outlet />}</ForecastContext.Provider>
}
